// S:/Instrumentacion/src/pages/dimensional-longitudinal/LongitudinalCapture.jsx
import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// 👇 Relative imports (sin alias @)
import { supabase } from '../../utils/supabase';
import { DimensionalLongitudinal } from '../../utils/dimensional-templates';
import { createDimensionalCalibration, closeDimensionalCalibration } from '../../utils/api-dim-calibrations';
import { saveDimensionalRows } from '../../utils/api-dim-measurements';

const num = v => (v === '' || v == null) ? '' : Number(v);

export default function LongitudinalCapture() {
  const { instrumentId } = useParams();
  const navigate = useNavigate();

  const [instrument, setInstrument] = useState(null);
  const [subtypeKey, setSubtypeKey] = useState('caliper');
  const [calibration, setCalibration] = useState(null);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [closing, setClosing] = useState(false);

  const subtype = useMemo(() => DimensionalLongitudinal.subtypes[subtypeKey], [subtypeKey]);

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from('instruments')
        .select('id, tag, brand, model, serial, unit, range_min, range_max, resolution')
        .eq('id', instrumentId).single();
      if (error) { alert('No se pudo cargar el instrumento'); return; }
      setInstrument(data);
      const pts = subtype.suggestSetpoints?.(data.range_min, data.range_max) ?? [];
      const base = { setpoint: '', r1: '', r2: '', r3: '' };
      setRows(pts.length ? pts.map(sp => ({ ...base, setpoint: sp })) : [base, base, base, base, base]);
    })();
  }, [instrumentId, subtypeKey]);

  const updateCell = (i, key, val) => setRows(rs => {
    const copy = [...rs]; copy[i] = { ...copy[i], [key]: val }; return copy;
  });
  const addRow = () => setRows(rs => [...rs, { setpoint: '', r1: '', r2: '', r3: '' }]);

  async function ensureCalibration() {
    if (calibration?.id) return calibration;
    const cal = await createDimensionalCalibration(instrumentId, { procedure: 'DIM-LONG v1' });
    setCalibration(cal); return cal;
  }

  async function handleSave() {
    try {
      setSaving(true);
      const cal = await ensureCalibration();
      await saveDimensionalRows({
        calibrationId: cal.id,
        unit: subtype.unit,                       // unidad bloqueada por plantilla
        rows,
        resolution: instrument?.resolution ?? 0, // u_res = res/√12
        k: 2,
      });
      alert('Mediciones guardadas');
    } catch (e) {
      console.error(e); alert('No se pudieron guardar las mediciones');
    } finally {
      setSaving(false);
    }
  }

  async function handleClose() {
    if (!calibration?.id) { alert('Guardá antes de cerrar'); return; }
    if (!confirm('¿Cerrar calibración y generar certificado?')) return;
    try {
      setClosing(true);
      await closeDimensionalCalibration(calibration.id);
      alert('Calibración cerrada. Podés descargar el certificado desde la ficha.');
      navigate(`/instrument-details/${instrumentId}`);
    } catch (e) {
      console.error(e); alert('No se pudo cerrar la calibración');
    } finally { setClosing(false); }
  }

  if (!instrument) return <div className="p-6">Cargando…</div>;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold">Dimensional → Longitudinal</h1>
        <p className="text-gray-600">
          Instrumento: <b>{instrument.tag || instrument.id}</b> — {instrument.brand} {instrument.model} (SN {instrument.serial || 'N/D'})
        </p>
      </header>

      <div className="flex items-center gap-3 mb-4">
        <label className="text-sm font-medium">Subtipo</label>
        <select className="border rounded px-3 py-2" value={subtypeKey} onChange={(e)=>setSubtypeKey(e.target.value)}>
          {Object.entries(DimensionalLongitudinal.subtypes).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <div className="ml-auto text-sm">
          Unidad: <b>{subtype.unit}</b> (bloqueada)
        </div>
      </div>

      <div className="border rounded overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              {subtype.columns.map(c => <th key={c.id} className="text-left px-3 py-2">{c.label}</th>)}
              <th className="text-left px-3 py-2">Media</th>
              <th className="text-left px-3 py-2">Error</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const readings = ['r1','r2','r3','r4'].map(k => r[k]).filter(v => v !== '' && v != null).map(Number);
              const m = readings.length ? readings.reduce((a,b)=>a+b,0)/readings.length : null;
              const err = m != null && r.setpoint !== '' ? m - Number(r.setpoint) : null;
              return (
                <tr key={i} className="border-t">
                  {subtype.columns.map(c => (
                    <td key={c.id} className="px-3 py-2">
                      <input
                        type="number" step="any"
                        className="border rounded px-2 py-1 w-40"
                        value={num(r[c.id])}
                        onChange={(e)=>updateCell(i, c.id, e.target.value)}
                      />
                    </td>
                  ))}
                  <td className="px-3 py-2">{m == null ? '—' : m.toFixed(4)}</td>
                  <td className="px-3 py-2">{err == null ? '—' : `${err.toFixed(4)} ${subtype.unit}`}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-3 mt-4">
        <button onClick={addRow} className="px-4 py-2 border rounded hover:bg-gray-50">+ Agregar fila</button>
        <button onClick={handleSave} disabled={saving} className="ml-auto px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-60">
          {saving ? 'Guardando…' : 'Guardar mediciones'}
        </button>
        <button onClick={handleClose} disabled={closing || !calibration?.id} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60">
          {closing ? 'Cerrando…' : 'Cerrar calibración'}
        </button>
      </div>

      <p className="text-xs text-gray-500 mt-3">
        La repetibilidad se estima con 2–3 lecturas por punto. La incertidumbre combinada y U se recalculan al leer las mediciones (igual que hoy).
      </p>
    </div>
  );
}
